/**
 * ConnectNodesModal — AI Actions: "Connect Disconnected Nodes".
 *
 * Given the user-selected isolated nodes and the rest of the graph as
 * context, calls the AI to propose relationships that would connect each
 * isolated node into the main graph. The user reviews each suggestion,
 * accepts/rejects individually, then applies the accepted ones.
 */
import { useEffect, useState } from 'react';
import type { Node, Edge } from '@xyflow/react';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Checkbox,
  InlineLoading,
  InlineNotification,
  Button,
} from '@carbon/react';
import { ontologyGenerationApi } from '@/lib/api/ontology-generation.api';
import type { ConnectedNodeSuggestion } from '@shared/types/ontology';

interface ConnectNodesModalProps {
  isOpen: boolean;
  selectedNodes: Node[];
  allNodes: Node[];
  allEdges: Edge[];
  maxSuggestionsPerNode: number;
  userId: string;
  onAccept: (accepted: ConnectedNodeSuggestion[]) => void;
  onClose: () => void;
}

export function ConnectNodesModal({
  isOpen,
  selectedNodes,
  allNodes,
  allEdges,
  maxSuggestionsPerNode,
  userId,
  onAccept,
  onClose,
}: ConnectNodesModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<ConnectedNodeSuggestion[]>([]);
  const [accepted, setAccepted] = useState<Set<number>>(new Set());

  const runConnectNodes = () => {
    setLoading(true);
    setError(null);
    setSuggestions([]);
    setAccepted(new Set());

    ontologyGenerationApi
      .connectNodes({
        isolatedNodes: selectedNodes.map((n) => ({
          id: n.id,
          label: (n.data?.label as string) || n.id,
          definition: n.data?.definition as string | undefined,
        })),
        graphNodes: allNodes.map((n) => ({
          id: n.id,
          label: (n.data?.label as string) || n.id,
          definition: n.data?.definition as string | undefined,
        })),
        graphEdges: allEdges.map((e) => ({
          source: e.source,
          target: e.target,
          label: (e.data?.label as string) || e.label as string | undefined,
        })),
        maxSuggestionsPerNode,
        userId,
      })
      .then((response) => {
        setSuggestions(response.suggestions);
        setAccepted(new Set(response.suggestions.map((_, index) => index)));
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message || 'Failed to get AI suggestions');
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isOpen) {
      runConnectNodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const toggleAccepted = (index: number) => {
    setAccepted((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const findLabel = (id: string): string => {
    const node = allNodes.find((n) => n.id === id) || selectedNodes.find((n) => n.id === id);
    return (node?.data?.label as string) || id;
  };

  const handleApply = () => {
    const acceptedSuggestions = suggestions.filter((_, index) => accepted.has(index));
    onAccept(acceptedSuggestions);
    onClose();
  };

  return (
    <ComposedModal open={isOpen} onClose={onClose} size="lg">
      <ModalHeader
        title="Connect Disconnected Nodes"
        label="AI-proposed relationships"
      />
      <ModalBody>
        {loading && (
          <div style={{ marginBottom: '1rem' }}>
            <InlineLoading description="AI is analysing…" status="active" />
          </div>
        )}

        {error && (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={error}
            lowContrast
            hideCloseButton
            style={{ marginBottom: '1rem' }}
          />
        )}

        {!loading && !error && (
          <>
            {suggestions.length === 0 && (
              <InlineNotification
                kind="info"
                title="No suggestions"
                subtitle="The AI did not propose any relationships for the selected nodes."
                lowContrast
                hideCloseButton
                style={{ marginBottom: '1rem' }}
              />
            )}
            {suggestions.map((suggestion, index) => {
              const isolatedLabel = findLabel(suggestion.isolatedNodeId);
              const targetLabel = findLabel(suggestion.targetNodeId);
              const [fromLabel, toLabel] =
                suggestion.direction === 'to-isolated'
                  ? [targetLabel, isolatedLabel]
                  : [isolatedLabel, targetLabel];

              return (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    padding: '0.75rem 0',
                    borderBottom: '1px solid var(--cds-border-subtle)',
                  }}
                >
                  <Checkbox
                    id={`connect-nodes-suggestion-${index}`}
                    labelText=""
                    checked={accepted.has(index)}
                    onChange={() => toggleAccepted(index)}
                  />
                  <div style={{ flex: 1, marginLeft: '0.5rem' }}>
                    <div>
                      <strong>{fromLabel}</strong>
                      {' — '}
                      {suggestion.label}
                      {' → '}
                      <strong>{toLabel}</strong>
                    </div>
                    {suggestion.rationale && (
                      <div style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>
                        {suggestion.rationale}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}

        {error && (
          <Button kind="tertiary" onClick={runConnectNodes} style={{ marginTop: '0.5rem' }}>
            Retry
          </Button>
        )}
      </ModalBody>
      <ModalFooter
        primaryButtonText="Apply selected"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleApply}
        onRequestClose={onClose}
        primaryButtonDisabled={loading || !!error || accepted.size === 0}
      >
        <></>
      </ModalFooter>
    </ComposedModal>
  );
}

// Made with Bob
